import type { ScreenerCandle, ScreenerOIDataPoint, ScreenerTradePriceCandle } from '@/types'
import type { ScalePoint } from './chartScale'
import { findLastKnownIndex, isPlottableOI } from './chartUtils'

export interface PricePoint {
  timestamp: number
  price: number | null
  candle?: ScreenerTradePriceCandle
}

export function buildPricePlot({ oiData, candles, tradeHistory, native }: {
  oiData: readonly ScreenerOIDataPoint[]
  candles: readonly ScreenerCandle[]
  tradeHistory: readonly ScreenerTradePriceCandle[]
  native: boolean
}) {
  let source: string
  let points: PricePoint[]
  if (native) {
    source = 'Trade candles'
    points = tradeHistory.filter((point) => Number.isFinite(point.timestamp)).map((candle) => ({
      timestamp: candle.timestamp,
      price: candle.close != null && Number.isFinite(candle.close) && candle.close > 0 ? candle.close : null,
      candle,
    }))
  } else {
    const aligned = oiData.filter((point) => isPlottableOI(point) && Number.isFinite(point.timestamp)
      && Number.isFinite(point.price) && Number(point.price) > 0)
      .map((point) => ({ timestamp: point.timestamp, price: Number(point.price) }))
    const fallback = candles.filter((point) => Number.isFinite(point.timestamp)
      && Number.isFinite(point.close) && point.close > 0)
      .map((point) => ({ timestamp: point.timestamp, price: point.close }))
    const useAligned = aligned.length > 1 || fallback.length === 0
    points = useAligned ? aligned : fallback
    source = useAligned ? 'OI aligned' : 'Candle close'
  }
  points.sort((a, b) => a.timestamp - b.timestamp)
  const series: [number, number | null][] = points.map((point) => [point.timestamp * 1000, point.price])
  const candleSeries: [number, number | null, number | null, number | null, number | null][] = []
  const candleScale: ScalePoint[] = []
  for (const point of points) {
    const candle = point.candle
    const values = candle && [candle.open, candle.close, candle.low, candle.high]
    if (!values || values.some((value) => value == null || !Number.isFinite(value) || value <= 0)
      || candle!.low! > Math.min(candle!.open!, candle!.close!)
      || candle!.high! < Math.max(candle!.open!, candle!.close!)) {
      candleSeries.push([point.timestamp * 1000, null, null, null, null])
      candleScale.push([point.timestamp * 1000, null])
    } else {
      candleSeries.push([point.timestamp * 1000, candle!.open, candle!.close, candle!.low, candle!.high])
      candleScale.push([point.timestamp * 1000, candle!.low], [point.timestamp * 1000, candle!.high])
    }
  }
  return { points, timestamps: points.map((point) => point.timestamp * 1000), source,
    series, candleSeries, candleScaleSeries: [candleScale], scaleSeries: [series] }
}

export function pricePointAtTime(plot: ReturnType<typeof buildPricePlot>, timestampMs: number): PricePoint | null {
  const index = findLastKnownIndex(plot.timestamps, timestampMs)
  const point = index < 0 ? null : plot.points[index]
  if (!point || point.price == null) return null
  // A recorded bar owns only its actual retained interval. Hovering an outage
  // must not silently select the preceding price candle.
  return point.candle && timestampMs >= (point.timestamp + point.candle.interval_seconds) * 1000 ? null : point
}
